import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/calendar.css";

const AvailabilityCheck = ({ onAvailable }) => {
  const [hall, setHall] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [freeSlots, setFreeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  console.log("API URL:", API_URL);

  /* ================= HALLS ================= */
  useEffect(() => {
    fetch(`${API_URL}/api/halls`)
      .then((res) => res.json())
      .then(setHalls);
  }, []);

  /* ================= BOOKINGS ================= */
  useEffect(() => {
    if (!hall) return;

    const fetchBookedDates = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/bookings/booked-dates?hallId=${hall}`,
        );
        const data = await res.json();

        const formatted = data.map((d) => new Date(d.EventDate).toDateString());

        setBookedDates(formatted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookedDates();
  }, [hall]);

  /* ================= DATE SELECT ================= */
  const handleDateClick = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);

    const formatLocalDate = (date) => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(date.getDate()).padStart(2, "0")}`;
    };

    const formattedDate = formatLocalDate(date);

    try {
      const res = await fetch(
        `${API_URL}/api/bookings/booked-slots?hallId=${hall}&date=${formattedDate}`,
      );

      const data = await res.json();

      setBookedSlots(data);

      const free = getFreeSlots(data);
      setFreeSlots(free);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= CALENDAR STYLING ================= */
  const tileClassName = ({ date, view }) => {
    if (view !== "month") return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const formatted = date.toDateString();

    // ✅ FIRST: past check
    if (date < today) return "past-date";

    // ✅ THEN: booked
    if (bookedDates.includes(formatted)) return "booked-date";

    // ✅ selected
    if (selectedDate && date.toDateString() === selectedDate.toDateString())
      return "selected-date";

    return "available-date";
  };

  const tileDisabled = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return date < today; // ❌ remove booked from here
  };

  const toMinutes = (time) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;

    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;

    return `${hour}:${m === 0 ? "00" : m} ${ampm}`;
  };

  const getFreeSlots = (bookings) => {
    const DAY_START = 0;
    const DAY_END = 1440;

    const sorted = bookings
      .map((b) => ({
        start: Math.floor(toMinutes(b.StartTime) / 60) * 60,
        end: Math.ceil(toMinutes(b.EndTime) / 60) * 60,
      }))
      .sort((a, b) => a.start - b.start);

    const free = [];
    let lastEnd = DAY_START;

    for (let b of sorted) {
      if (b.start > lastEnd) {
        free.push({ start: lastEnd, end: b.start });
      }
      lastEnd = Math.max(lastEnd, b.end);
    }

    if (lastEnd < DAY_END) {
      free.push({ start: lastEnd, end: DAY_END });
    }

    return free;
  };
  const formatLocalDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };
  return (
    <div className="min-h-[80vh] flex justify-center items-center bg-gray-100 p-6">
      <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-4xl">
        {/* HEADER */}
        <h2 className="text-2xl font-bold text-center mb-6">
          Check Availability
        </h2>

        {/* HALL SELECT */}
        <div className="mb-6">
          {/* <label className="block text-sm text-gray-500 mb-2">
            Select Hall
          </label> */}

          <select
            value={hall}
            onChange={(e) => {
              setHall(e.target.value);
              setSelectedDate(null);
            }}
            className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">Select Hall</option>
            {halls.map((h) => (
              <option key={h.Id} value={h.Id}>
                {h.Name}
              </option>
            ))}
          </select>
        </div>

        {/* CALENDAR */}
        {hall && (
          <div className="flex flex-col items-center">
            {loading && (
              <div className="text-sm text-gray-500 mb-3">
                Loading bookings...
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-xl shadow-inner">
              <Calendar
                onClickDay={handleDateClick}
                tileClassName={tileClassName}
                tileDisabled={tileDisabled}
                value={selectedDate}
              />
            </div>

            {/* SELECTED INFO */}
            {selectedDate && (
              <div className="mt-4 text-sm text-gray-600">
                Selected:{" "}
                <span className="font-semibold text-teal-600">
                  {selectedDate.toDateString()}
                </span>
              </div>
            )}
          </div>
        )}

        {selectedDate && (
          <div className="mt-6 w-full space-y-6">
            {/* OCCUPIED */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="text-red-600 font-semibold mb-3 flex items-center gap-2">
                🔴 Occupied Time
              </h3>

              {bookedSlots.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No bookings for this date
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {bookedSlots.map((b, i) => (
                    <span
                      key={i}
                      className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {formatTime(Math.floor(toMinutes(b.StartTime) / 60) * 60)}{" "}
                      -{formatTime(Math.ceil(toMinutes(b.EndTime) / 60) * 60)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* AVAILABLE */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h3 className="text-green-700 font-semibold mb-3 flex items-center gap-2">
                🟢 Available Time Slots
              </h3>

              {freeSlots.length === 0 ? (
                <p className="text-red-500 font-medium">Fully Booked</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {freeSlots.map((slot, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-3 rounded-xl text-sm font-medium transition-all ${
                        selectedSlot === slot
                          ? "bg-teal-600 text-white shadow-md scale-105"
                          : "bg-white border border-green-300 hover:bg-green-500 hover:text-white"
                      }`}
                    >
                      {formatTime(slot.start)} - {formatTime(slot.end)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedSlot && (
          <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-xl text-center">
            <p className="text-sm text-gray-600 mb-1">Selected Time</p>

            <p className="font-semibold text-blue-700">
              {formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)}
            </p>

            <button
              onClick={() => {
                const formattedDate = formatLocalDate(selectedDate);

                onAvailable({
                  hallId: Number(hall),
                  eventDate: formattedDate,
                  startTime: formatTime(selectedSlot.start),
                  endTime: formatTime(selectedSlot.end),
                });
              }}
              className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              Proceed →
            </button>
          </div>
        )}

        {/* LEGEND */}
        <div className="mt-6 flex justify-center gap-8 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-teal-500 rounded-full"></span>
            Selected
          </div>

          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-red-400 rounded-full"></span>
            Booked
          </div>

          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-gray-300 rounded-full"></span>
            Past
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCheck;
